import { Badge } from '../ui/badge';
import { summarise } from '../../utils/round-score-summary';
import type { ScoredAttempt } from '../../types/competition-round';

interface BoulderAttemptMarkersProps {
  attempts: ScoredAttempt[];
}

/**
 * A boulder as it reads on a scorecard: one mark per attempt in the order they
 * happened (`|||+T`), then the score they add up to (`T5 Z4`, see `summarise`).
 * Both are read off the marks alone, the way the ranking reads them.
 *
 * Two fragments, not a box: every caller already has its own row to lay them
 * out in. Every mark is drawn — the row is the full width of the page, and a
 * long card wraps onto a second line rather than losing its first goes behind
 * an ellipsis.
 */
export function BoulderAttemptMarkers({ attempts }: BoulderAttemptMarkersProps) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-0.5 font-mono text-sm">
        {attempts.map((a) => {
          if (a.attempt_type === 'zone') {
            return <span key={a.id} className="text-yellow-600 font-bold" data-testid="zone-marker">+</span>;
          }
          if (a.attempt_type === 'top') {
            return <span key={a.id} className="text-green-600 font-bold" data-testid="top-marker">T</span>;
          }
          return <span key={a.id} className="text-muted-foreground" data-testid="attempt-marker">|</span>;
        })}
      </div>

      <Badge variant="outline" className="text-xs font-mono shrink-0" data-testid="boulder-summary">
        {summarise(attempts.map((a) => a.attempt_type))}
      </Badge>
    </>
  );
}
